import { useEffect, useMemo, useRef, useState } from "react";
import { Play } from "lucide-react";
import { controlsListFor, embedSrc, type HeroVideo } from "@/lib/heroVideo";

/**
 * The player is mounted only after the visitor clicks play, so page load never
 * touches the video file (or the cookie banner's auto blocking of media).
 * If the file fails, the card falls back to the cover image.
 */
export default function HeroVideoCard({ video }: { video: HeroVideo }) {
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);
  const label = video.caption || "Play video";
  const isUpload = video.source === "upload" && !!video.video_url;
  const isEmbed = video.source === "embed" && !!video.provider && !!video.video_id;

  useEffect(() => {
    if (playing && isUpload && ref.current) {
      ref.current.play().catch(() => undefined);
    }
  }, [playing, isUpload]);

  const showPlayer = playing && !failed;

  // Subtitles live on the video row; serve them to the player as a local file.
  const vtt = video.subtitles_vtt?.trim();
  const trackUrl = useMemo(
    () => (showPlayer && vtt ? URL.createObjectURL(new Blob([vtt], { type: "text/vtt" })) : null),
    [showPlayer, vtt],
  );
  useEffect(() => () => { if (trackUrl) URL.revokeObjectURL(trackUrl); }, [trackUrl]);

  return (
    <figure className="relative w-full">
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-seabed-foreground/15 bg-seabed shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6),0_0_0_1px_hsl(var(--primary)/0.12)]">
        {showPlayer && isUpload ? (
          <video
            ref={ref}
            src={video.video_url ?? undefined}
            poster={video.poster_url ?? undefined}
            data-cookieconsent="ignore"
            controls
            controlsList={controlsListFor(video) || undefined}
            disablePictureInPicture={video.allow_pip === false}
            disableRemotePlayback={video.allow_remote_playback === false}
            onContextMenu={video.allow_download ? undefined : (e) => e.preventDefault()}
            playsInline
            preload="auto"
            onError={() => {
              setFailed(true);
              setPlaying(false);
            }}
            className="hero-video absolute inset-0 h-full w-full object-cover"
          >
            {trackUrl && (
              <track kind="subtitles" srcLang="en" label="English" default={video.subtitles_default_on !== false} src={trackUrl} />
            )}
          </video>
        ) : showPlayer && isEmbed ? (
          <iframe
            src={embedSrc(video.provider!, video.video_id!)}
            title={label}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : video.poster_url ? (
          <img src={video.poster_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : null}
        {!showPlayer && !failed && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={label}
            className="group absolute inset-0 flex items-center justify-center bg-seabed/15 focus-visible:outline-none"
          >
            <span className="relative flex h-[72px] w-[72px] items-center justify-center">
              <span aria-hidden className="absolute inset-0 rounded-full ring-1 ring-seabed-foreground/40 animate-ping [animation-duration:2.6s] motion-reduce:hidden" />
              <span className="relative flex h-[72px] w-[72px] items-center justify-center rounded-full bg-seabed-foreground/[0.14] ring-1 ring-seabed-foreground/30 backdrop-blur-md transition-transform duration-300 group-hover:scale-[1.06] group-hover:ring-seabed-foreground/60 group-focus-visible:ring-2 group-focus-visible:ring-seabed-foreground group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-seabed motion-reduce:transition-none">
                <Play className="ml-[2px] h-7 w-7 text-seabed-foreground" fill="currentColor" strokeWidth={0} />
              </span>
            </span>
            <span className="absolute bottom-4 left-4 rounded-full bg-seabed-foreground/[0.14] px-3 py-1.5 text-xs font-medium text-seabed-foreground ring-1 ring-seabed-foreground/30 backdrop-blur-md">
              Watch the overview
            </span>
          </button>
        )}
      </div>
      {video.caption && (
        <figcaption className="mt-4 text-sm text-seabed-foreground/75">{video.caption}</figcaption>
      )}
    </figure>
  );
}
